/**
 * Tags Management Utilities for Chat Tools
 * Handles conversion between storage format (comma-separated string) and API format (array)
 */

import {
  parseListField,
  arrayToListString,
  listStringToArray,
  validateListItem,
  validateListItems,
} from "../json/list-field-utils";

/**
 * Parse tags from storage format (comma-separated string or array)
 * Returns array of tag strings
 */
export function parseTagsFromStorage(value: any): string[] {
  return parseListField(value);
}

/**
 * Convert tags array to storage format (comma-separated string)
 * This is what gets stored in the database
 */
export function tagsArrayToStorageString(tags: string[]): string {
  return arrayToListString(tags);
}

/**
 * Convert tags string to array for storage
 * This is what gets sent to the database
 */
export function tagsStringToArray(value: string): string[] {
  return listStringToArray(value);
}

/**
 * Validate a single tag
 */
export function validateTag(tag: string): { valid: boolean; error?: string } {
  const trimmed = tag.trim();

  if (trimmed.length === 0) {
    return { valid: false, error: "Tag cannot be empty" };
  }

  if (trimmed.length > 50) {
    return { valid: false, error: "Tag cannot exceed 50 characters" };
  }

  // Tags should be alphanumeric with hyphens and underscores
  if (!/^[a-zA-Z0-9\-_\s]+$/.test(trimmed)) {
    return {
      valid: false,
      error: "Tag can only contain letters, numbers, hyphens, underscores, and spaces",
    };
  }

  return { valid: true };
}

/**
 * Validate all tags
 */
export function validateTags(tags: string[]): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  let hasErrors = false;

  tags.forEach((tag, index) => {
    const validation = validateTag(tag);
    if (!validation.valid) {
      errors[`tag-${index}`] = validation.error || "Invalid tag";
      hasErrors = true;
    }
  });

  return { valid: !hasErrors, errors };
}

/**
 * Normalize tags for storage
 * - Trim whitespace
 * - Convert to lowercase
 * - Remove duplicates
 * - Sort alphabetically
 */
export function normalizeTags(tags: string[]): string[] {
  return Array.from(new Set(tags.map((tag) => tag.trim().toLowerCase())))
    .filter((tag) => tag.length > 0)
    .sort();
}

/**
 * Format tags for display
 * - Capitalize first letter of each word
 */
export function formatTagsForDisplay(tags: string[]): string[] {
  return tags.map((tag) =>
    tag
      .split(/[\s\-_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  );
}

/**
 * Parse tags from user input (comma-separated string)
 * Handles normalization and validation
 */
export function parseUserTagInput(input: string): {
  tags: string[];
  errors: Record<string, string>;
  valid: boolean;
} {
  const tags = parseListField(input);
  const normalized = normalizeTags(tags);
  const validation = validateTags(normalized);

  return {
    tags: normalized,
    errors: validation.errors,
    valid: validation.valid,
  };
}

/**
 * Merge tags from multiple sources, removing duplicates
 */
export function mergeTags(...tagArrays: string[][]): string[] {
  const merged = tagArrays.flat();
  return normalizeTags(merged);
}

/**
 * Filter tags by search term
 */
export function filterTags(tags: string[], searchTerm: string): string[] {
  const term = searchTerm.toLowerCase();
  return tags.filter((tag) => tag.toLowerCase().includes(term));
}

/**
 * Get common tags from multiple tag arrays
 */
export function getCommonTags(...tagArrays: string[][]): string[] {
  if (tagArrays.length === 0) return [];
  if (tagArrays.length === 1) return tagArrays[0];

  const [first, ...rest] = tagArrays;
  return first.filter((tag) => rest.every((arr) => arr.includes(tag)));
}

/**
 * Get unique tags from multiple tag arrays
 */
export function getUniqueTags(...tagArrays: string[][]): string[] {
  return normalizeTags(tagArrays.flat());
}
